import { useCustomCss } from '../lib/customCss';
import { Camera, Coffee, Eye, Flag, Hand, Users, Mic, Monitor, MonitorUp, ShieldCheck, Volume2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { kidColor } from '../lib/palette';
import { deviceApi, tokens } from '../lib/api';
import { blurChoice, blurTrack, canBlur, saveBlurChoice, type BlurredTrack } from '../lib/blur';
import { clock, monthDayLabel, platformName, policyLabel } from '../lib/format';
import type { SignalData } from '../lib/rtc';
import { Socket } from '../lib/socket';
import type { Policy } from '../lib/types';
import { Logo } from '../ui/Logo';
import { Whiteboard } from '../parent/Whiteboard';
import { ChatThread, type ChatMsg } from '../parent/Chat';
import { AgentPeers, type LocalTracks, type Viewer } from './AgentPeers';

interface Kid {
  id: string;
  name: string;
  color: string;
}

/** The page a child's computer keeps open: pairing first, then the school session. */
export function AgentApp() {
  useCustomCss('agent'); // an admin's custom styles (#367)
  const [token, setToken] = useState(tokens.device());
  if (!token) return <PairScreen onPaired={setToken} />;
  return (
    <AgentSession
      token={token}
      onUnpaired={() => {
        tokens.setDevice(null);
        setToken(null);
      }}
    />
  );
}

function PairScreen({ onPaired }: { onPaired: (token: string) => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState(`${platformName()} computer`);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function pair(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await deviceApi.post<{ token: string }>('/api/device/pair', {
        code: code.replace(/\D/g, ''),
        name,
        kind: 'agent',
        platform: platformName(),
      });
      tokens.setDevice(res.token);
      onPaired(res.token);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="agent auth">
      <div className="auth-card card">
        <Logo large />
        <h1>Set up this computer</h1>
        <p className="muted">
          Ask your parent for the 6-digit code from their ClassGuardian app. During school time, they’ll be able to see this
          screen, your camera and hear your microphone. A banner shows at the top whenever your camera or microphone is on.
        </p>
        <form className="stack" onSubmit={pair}>
          <label>
            Family code
            <input
              className="code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, '').slice(0, 7))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123 456"
              required
              autoFocus
            />
          </label>
          <label>
            Name for this computer
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} required />
          </label>
          {error && <p className="error">{error}</p>}
          <button className="btn primary big" disabled={busy || code.replace(/\D/g, '').length !== 6}>
            {busy ? 'Pairing…' : 'Pair'}
          </button>
        </form>
      </div>
    </main>
  );
}


function chime() {
  try {
    const ctx = new AudioContext();
    [660, 880].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + i * 0.18);
      g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + i * 0.18 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + i * 0.18 + 0.4);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + i * 0.18);
      o.stop(ctx.currentTime + i * 0.18 + 0.45);
    });
    setTimeout(() => ctx.close(), 1500);
  } catch {
    /* sound is optional */
  }
}

function AgentSession({ token, onUnpaired }: { token: string; onUnpaired: () => void }) {
  const [kid, setKid] = useState<Kid | null>(null);
  // A computer the family shares (#442): the children who can pick it up, and whether to ask who's here.
  const [family, setFamily] = useState<Kid[]>([]);
  const [picking, setPicking] = useState(false);
  const askedRef = useRef(false);
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [connected, setConnected] = useState(false);
  const [notice, setNotice] = useState('');
  const [tracks, setTracks] = useState<LocalTracks>({ screen: null, camera: null, mic: null, audio: null });
  const [viewers, setViewers] = useState<Viewer[]>([]);
  // What each watching parent wants on — their mic / camera (#294, #930); the device opens a device only if some viewer wants it.
  const [want, setWant] = useState({ mic: false, camera: false });
  const listenRef = useRef<Map<string, { mic: boolean; camera: boolean }>>(new Map());
  const recomputeWant = useCallback(() => {
    let mic = false;
    let camera = false;
    for (const w of listenRef.current.values()) {
      mic = mic || w.mic;
      camera = camera || w.camera;
    }
    setWant((cur) => (cur.mic === mic && cur.camera === camera ? cur : { mic, camera }));
  }, []);
  const [parentVideo, setParentVideo] = useState<MediaStream | null>(null);
  const [whiteboardOpen, setWhiteboardOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [relaying, setRelaying] = useState(false);

  const socketRef = useRef<Socket | null>(null);
  const peersRef = useRef<AgentPeers | null>(null);
  const selfRef = useRef<HTMLVideoElement>(null);
  const parentVideoRef = useRef<HTMLVideoElement>(null);
  const tracksRef = useRef(tracks);
  tracksRef.current = tracks;

  const stopTracks = useCallback(() => {
    const t = tracksRef.current;
    [t.screen, t.camera, t.mic, t.audio].forEach((x) => x?.stop());
    setTracks({ screen: null, camera: null, mic: null, audio: null });
  }, []);

  // Connection to the server, and live view peers.
  useEffect(() => {
    const socket = new Socket(token, 'device');
    socketRef.current = socket;
    const peers = new AgentPeers(
      socket,
      // Refetch per viewer so time-limited TURN creds stay current (don't cache for the page's lifetime).
      async () => (await deviceApi.get<{ iceServers: RTCIceServer[] }>('/api/device/ice')).iceServers,
      (v, video) => {
        setViewers(v);
        setParentVideo(video);
      },
    );
    peersRef.current = peers;

    const offs = [
      socket.on('connection', (m) => setConnected(!!m.connected)),
      socket.on('hello', (m) => {
        setKid(m.child as Kid);
        setPolicy(m.policy as Policy);
        setNotice('');
        const fam = m.shared ? ((m.family as Kid[]) ?? []) : [];
        setFamily(fam);
        if (fam.length && !askedRef.current) {
          askedRef.current = true;
          setPicking(true);
        }
      }),
      socket.on('policy', (m) => setPolicy(m.policy as Policy)),
      // The parent answered "I'm done for today" (#440). Approved shows on its own through the new policy.
      socket.on('done-answer', (m) => {
        if (m.answer === 'declined') {
          setDoneAsked(0);
          setDoneNote('Your parent says keep going a little longer.');
        } else setDoneNote('');
      }),
      socket.on('signal', (m) => peers.signal(String(m.from), String(m.parentName ?? ''), m.data as SignalData)),
      socket.on('hangup', (m) => peers.close(String(m.from), false)),
      socket.on('talk', (m) => peers.talk(String(m.from), !!m.on, !!m.video)),
      // A parent turned their mic listening / camera viewing on or off (#294, #930): open those devices only on request.
      socket.on('listen', (m) => {
        listenRef.current.set(String(m.from), { mic: !!m.mic, camera: !!m.camera });
        recomputeWant();
      }),
      socket.on('whiteboard', (m) => {
        if (m.open === true) setWhiteboardOpen(true);
        else if (m.close === true) setWhiteboardOpen(false);
      }),
      socket.on('chat', (m) => {
        const msg: ChatMsg = { id: Number(m.activityId), from: m.from as 'parent' | 'child', name: String(m.name), text: String(m.text), ts: Number(m.ts) };
        setMessages((prev) => (prev.some((x) => x.id === msg.id) ? prev : [...prev, msg]));
        if (m.from === 'parent') chime();
      }),
      // Server-relay screen view (the parent's fallback when WebRTC can't connect).
      socket.on('relay-start', () => setRelaying(true)),
      socket.on('relay-stop', () => setRelaying(false)),
      socket.on('signed-out', () => onUnpaired()),
      socket.on('replaced', () => {
        peers.closeAll();
        stopTracks();
        setKid(null);
        setNotice('ClassGuardian was opened in another window, so this one stopped. Reload to use this one.');
      }),
    ];
    return () => {
      offs.forEach((off) => off());
      peers.closeAll();
      socket.close();
    };
  }, [token, onUnpaired, stopTracks, recomputeWant]);

  // Camera blur (#592): when the parent allows it and the child turns it on, a blurred copy of the camera is what's
  // sent and previewed. Until it's ready nothing is sent from the camera, so the room never shows by accident.
  const blurAllowed = !!policy?.blurAllowed && canBlur();
  const [blurPick, setBlurPick] = useState(() => blurChoice('cg.blurChild'));
  const blurOn = blurAllowed && (blurPick ?? !!policy?.blurDefault);
  const [blurred, setBlurred] = useState<BlurredTrack | null>(null);
  useEffect(() => {
    if (!blurOn || !tracks.camera) return;
    let live = true;
    let made: BlurredTrack | null = null;
    blurTrack(tracks.camera)
      .then((b) => {
        made = b;
        if (live) setBlurred(b);
        else b.stop();
      })
      .catch(() => live && setNotice('Background blur isn’t available in this browser.'));
    return () => {
      live = false;
      made?.stop();
      setBlurred(null);
    };
  }, [blurOn, tracks.camera]);
  const sentCamera = !tracks.camera ? null : blurOn ? (blurred?.track ?? null) : tracks.camera;

  // Tell open live views and the parent what is being shared.
  useEffect(() => {
    peersRef.current?.setTracks({ ...tracks, camera: sentCamera });
    socketRef.current?.send({
      type: 'status',
      status: { screen: !!tracks.screen, camera: !!tracks.camera, mic: !!tracks.mic, audio: !!tracks.audio, viewers: viewers.length, blurred: !!sentCamera && blurOn },
    });
    if (selfRef.current) selfRef.current.srcObject = sentCamera ? new MediaStream([sentCamera]) : null;
  }, [tracks, sentCamera, blurOn, viewers.length, connected]);

  useEffect(() => {
    if (parentVideoRef.current) parentVideoRef.current.srcObject = parentVideo;
  }, [parentVideo]);

  // Breaks and free time stop everything: nothing is captured or shared.
  useEffect(() => {
    if (policy && !policy.monitoring) {
      peersRef.current?.closeAll(policy.onBreak ? 'Break time started.' : 'School time is over.');
      stopTracks();
    }
  }, [policy, stopTracks]);

  const sharing = !!(tracks.screen || tracks.camera || tracks.mic);
  useEffect(() => {
    document.title = sharing ? '● Sharing · ClassGuardian' : 'ClassGuardian';
  }, [sharing]);

  async function startSharing() {
    setNotice('');
    let screen: MediaStream | null = null;
    try {
      screen = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'monitor', frameRate: { ideal: 15, max: 30 } },
        audio: true,
        // Chrome and Edge options: offer the whole screen and the computer's sound.
        systemAudio: 'include',
        selfBrowserSurface: 'exclude',
        monitorTypeSurfaces: 'include',
      } as DisplayMediaStreamOptions);
    } catch {
      setNotice('Screen sharing didn’t start. Press the button again and choose your entire screen.');
    }
    // The camera and microphone are off until a parent asks for them (#930): a fresh session is screen-only, so the
    // webcam light stays off and ghost mode (#928) applies. acquireMedia below opens them on request.
    const next: LocalTracks = {
      screen: screen?.getVideoTracks()[0] ?? null,
      audio: screen?.getAudioTracks()[0] ?? null,
      camera: null,
      mic: null,
    };
    for (const [key, track] of Object.entries(next) as [keyof LocalTracks, MediaStreamTrack | null][]) {
      track?.addEventListener('ended', () => setTracks((t) => (t[key] === track ? { ...t, [key]: null } : t)));
    }
    const old = tracksRef.current;
    [old.screen, old.camera, old.mic, old.audio].forEach((t) => t?.stop());
    setTracks(next);
  }

  // The camera opens only while a parent is viewing it, the microphone only while one is listening (#294, #930): each
  // parent's live view sends a `listen` with what it wants, and the device opens a device only if some viewer wants it.
  // Drop what a parent who has left wanted, so their device turns off when the last viewer who wanted it goes.
  useEffect(() => {
    const ids = new Set(viewers.map((v) => v.connId));
    let changed = false;
    for (const id of [...listenRef.current.keys()]) {
      if (!ids.has(id)) {
        listenRef.current.delete(id);
        changed = true;
      }
    }
    if (changed) recomputeWant();
  }, [viewers, recomputeWant]);

  // Open or close the camera / microphone to match what viewers want, but only during an active screen session.
  const mediaBusy = useRef({ camera: false, mic: false });
  const acquireMedia = useCallback(async (kind: 'camera' | 'mic') => {
    if (mediaBusy.current[kind]) return;
    mediaBusy.current[kind] = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        kind === 'camera' ? { video: { width: { ideal: 640 }, height: { ideal: 480 } } } : { audio: { echoCancellation: true, noiseSuppression: true } },
      );
      const track = kind === 'camera' ? stream.getVideoTracks()[0] : stream.getAudioTracks()[0];
      if (!track) return;
      track.addEventListener('ended', () => setTracks((t) => (t[kind] === track ? { ...t, [kind]: null } : t)));
      setTracks((t) => {
        if (t[kind]) t[kind]!.stop();
        return { ...t, [kind]: track };
      });
    } catch {
      setNotice(kind === 'camera' ? 'Allow the camera on this computer so your parent can see it.' : 'Allow the microphone on this computer so your parent can hear it.');
    } finally {
      mediaBusy.current[kind] = false;
    }
  }, []);
  useEffect(() => {
    for (const kind of ['camera', 'mic'] as const) {
      if (want[kind] && !!tracks.screen && !tracks[kind]) void acquireMedia(kind);
      else if ((!want[kind] || !tracks.screen) && tracks[kind]) {
        tracks[kind]!.stop();
        setTracks((t) => ({ ...t, [kind]: null }));
      }
    }
  }, [want, tracks, acquireMedia]);

  // Load recent chat so the thread survives a reload.
  useEffect(() => {
    deviceApi.get<{ messages: ChatMsg[] }>('/api/device/chat').then((r) => setMessages(r.messages)).catch(() => {});
  }, [token]);

  // While a parent is watching via the server relay, send ~3 screen frames a second over the WebSocket
  // (a scaled base64 JPEG each). This is the fallback path when WebRTC can't connect (e.g. a hotel network).
  useEffect(() => {
    const screen = tracks.screen;
    if (!relaying || !screen) return;
    const video = document.createElement('video');
    video.srcObject = new MediaStream([screen]);
    video.muted = true;
    video.play().catch(() => {});
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    let stopped = false;
    let busy = false;
    let frameSeq = 0;
    const tick = () => {
      if (stopped || busy || !ctx || video.videoWidth === 0) return;
      busy = true;
      const scale = Math.min(1, 1280 / video.videoWidth);
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          if (!blob || stopped) return void (busy = false);
          const reader = new FileReader();
          reader.onload = () => {
            const dataUrl = String(reader.result);
            const b64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
            // Split each frame into small WebSocket messages (~48 KB). A whole frame can exceed proxy/CDN
            // per-message limits (e.g. Cloudflare) and get dropped; small chunks always get through. The
            // parent reassembles them by frameId.
            const CHUNK = 48 * 1024;
            const n = Math.ceil(b64.length / CHUNK) || 1;
            const frameId = ++frameSeq;
            for (let i = 0; i < n; i++) {
              socketRef.current?.send({ type: 'relay-frame', frameId, i, n, w: canvas.width, h: canvas.height, data: b64.slice(i * CHUNK, (i + 1) * CHUNK) });
            }
            busy = false;
          };
          reader.onerror = () => void (busy = false);
          reader.readAsDataURL(blob);
        },
        'image/jpeg',
        0.5,
      );
    };
    const id = window.setInterval(tick, 350);
    return () => {
      stopped = true;
      clearInterval(id);
      video.srcObject = null;
    };
  }, [relaying, tracks.screen]);

  const wbSend = useCallback((payload: object) => socketRef.current?.send({ type: 'whiteboard', ...payload }), []);
  const wbSubscribe = useCallback((fn: (m: { type: string; [k: string]: unknown }) => void) => socketRef.current?.on('whiteboard', fn) ?? (() => {}), []);
  const sendChat = useCallback((text: string) => void socketRef.current?.send({ type: 'chat', text }), []);

  // "I'm done for today" (#440): finished early, so ask the parent to end school time until tomorrow.
  const [doneAsked, setDoneAsked] = useState(0);
  const [doneNote, setDoneNote] = useState('');
  function askDone() {
    if (socketRef.current?.send({ type: 'done-request' })) {
      setDoneAsked(Date.now());
      setDoneNote('');
    }
  }

  /** Whether the parent shows a feature for this child (#454); older servers send no list. */
  const shows = (f: string) => !policy?.features || policy.features.includes(f);

  // Ask the parent for help (#232); the server limits it to one a minute.
  const [helpAsked, setHelpAsked] = useState(0);
  function askHelp() {
    if (socketRef.current?.send({ type: 'help-request' })) setHelpAsked(Date.now());
  }

  if (!kid || !policy) {
    return (
      <main className="agent center-page">
        <Logo large />
        <p className="muted">{notice || (connected ? 'Loading…' : 'Connecting to ClassGuardian…')}</p>
      </main>
    );
  }

  // Ghost mode (#928): when the parent watches only the screen, don't show the watching banner. The camera and
  // microphone always show it, so sharing either keeps the banner on.
  const ghostScreenOnly = !!policy.ghostMode && !tracks.camera && !tracks.mic;
  const watching = (viewers.length > 0 || relaying) && !ghostScreenOnly;
  // If a parent actually talks, the child hears them, so the talking banner still shows — ghost mode only hides silent screen watching.
  const talking = viewers.filter((v) => v.talking);

  return (
    <main className="agent" style={{ ['--kid' as string]: kidColor(kid.color) }}>
      <div className={`agent-banner ${watching ? 'watching' : sharing ? 'sharing' : policy.onBreak ? 'break' : 'idle'}`} role="status">
        {watching ? (
          <>
            <Eye size={20} />{' '}
            {viewers.length > 0 ? (
              <>
                {viewers.map((v) => v.parentName).join(' and ')} {viewers.length > 1 ? 'are' : 'is'} watching now
              </>
            ) : (
              'A parent is watching now'
            )}
          </>
        ) : sharing ? (
          <>
            <ShieldCheck size={20} /> Sharing with your parent during school time
          </>
        ) : policy.onBreak ? (
          <>
            <Coffee size={20} /> Break time: nothing is shared
          </>
        ) : policy.schoolTime ? (
          <>
            <MonitorUp size={20} /> School time: not sharing yet
          </>
        ) : (
          <>
            <ShieldCheck size={20} /> Free time: nothing is shared
          </>
        )}
        {!connected && <span className="banner-note">Reconnecting…</span>}
      </div>

      {talking.length > 0 && (
        <div className="talking-banner">
          <Volume2 size={20} /> {talking.map((v) => v.parentName).join(' and ')} {talking.length > 1 ? 'are' : 'is'} talking
        </div>
      )}

      <div className="agent-body">
        <header className="agent-head">
          <Logo />
          <span className="muted">Hi {kid.name}! {policyLabel(policy)}</span>
          {family.length > 0 && (
            <button className="btn small ghost" onClick={() => setPicking(true)}>
              <Users size={15} /> Not you? Switch
            </button>
          )}
        </header>

        {picking && family.length > 0 && (
          <section className="card agent-card stack" aria-label="Who’s using this computer?">
            <h1>Who’s using this computer? 👋</h1>
            <p className="muted">Pick your name. Your schedule and School Resources follow you.</p>
            <div className="row-gap wrap">
              {family.map((k) => (
                <button
                  key={k.id}
                  className={`btn ${k.id === kid.id ? 'primary' : ''}`}
                  onClick={() => {
                    if (k.id !== kid.id) socketRef.current?.send({ type: 'switch-child', childId: k.id });
                    setPicking(false);
                  }}
                >
                  {k.name}
                  {k.id === kid.id ? ' · using it now' : ''}
                </button>
              ))}
            </div>
          </section>
        )}

        {notice && <p className="notice">{notice}</p>}
        {doneNote && <p className="notice">{doneNote}</p>}

        {policy.monitoring ? (
          <section className="card agent-card">
            <h1>{sharing ? 'You’re all set' : 'Ready for school?'}</h1>
            <ul className="share-list">
              <li className={tracks.screen ? 'on' : ''}>
                <Monitor size={20} /> Screen {tracks.screen ? 'shared' : 'not shared'}
              </li>
              <li className={tracks.camera ? 'on' : ''}>
                <Camera size={20} /> Camera {tracks.camera ? 'shared' : 'not shared'}
              </li>
              <li className={tracks.mic ? 'on' : ''}>
                <Mic size={20} /> Microphone {tracks.mic ? 'shared' : 'not shared'}
              </li>
              <li className={tracks.audio ? 'on' : ''}>
                <Volume2 size={20} /> Computer sound {tracks.audio ? 'shared' : 'not shared'}
              </li>
            </ul>
            {!tracks.screen && (
              <button className="btn primary big" onClick={startSharing}>
                <MonitorUp size={20} /> {sharing ? 'Share my screen again' : 'Start school session'}
              </button>
            )}
            <p className="muted small">
              Your parent can watch only during school time, and a banner shows at the top whenever your camera or
              microphone is on. Nothing is recorded.
            </p>
            <div className="row-between">
              {/* A parent can hide each request for this child (#454, #625). */}
              <span className="row-gap">
                {shows('help') && (
                  <button className="btn help-btn" onClick={askHelp} disabled={Date.now() - helpAsked < 60_000}>
                    <Hand size={18} /> {Date.now() - helpAsked < 60_000 ? 'Parent notified' : 'Ask for help'}
                  </button>
                )}
                {shows('done') && (
                  <button className="btn" onClick={askDone} disabled={!!doneAsked}>
                    <Flag size={18} /> {doneAsked ? 'Parent asked' : 'I’m done for today'}
                  </button>
                )}
              </span>
              {sharing && (
                <button className="btn danger-text" onClick={stopTracks}>
                  Stop sharing
                </button>
              )}
            </div>
          </section>
        ) : (
          <section className="card agent-card">
            <h1>{policy.doneForDay ? 'Great work today! 🎉' : policy.onBreak ? 'Enjoy your break!' : policy.holidayName ? policy.holidayName : policy.paused ? 'School is on pause' : 'No school right now'}</h1>
            <p className="muted">
              {policy.doneForDay
                ? 'Your parent said you’re done for today. Nothing is shared. School starts again with tomorrow’s schedule.'
                : policy.onBreak
                ? `Your screen, camera and microphone are off. Break ends at ${clock(policy.breakUntil)}.`
                : policy.paused
                  ? `Enjoy your time off! School starts again after ${policy.pauseEnd ? monthDayLabel(policy.pauseEnd) : 'the break'}. Nothing is shared.`
                  : 'Your screen, camera and microphone are not shared. You can leave this page open for next time.'}
            </p>
          </section>
        )}

        <div className="agent-media">
          {tracks.camera && (
            <figure>
              <video ref={selfRef} autoPlay playsInline muted className="mirror" />
              <figcaption>What your camera shows</figcaption>
            </figure>
          )}
          {tracks.camera && policy && (
            <label className="check agent-blur">
              <input
                type="checkbox"
                checked={blurOn}
                disabled={!blurAllowed}
                onChange={(e) => {
                  saveBlurChoice('cg.blurChild', e.target.checked);
                  setBlurPick(e.target.checked);
                }}
              />
              <span>{blurAllowed ? 'Blur my camera background' : 'Blur my camera background (turned off by your parent)'}</span>
            </label>
          )}
          {parentVideo && (
            <figure>
              <video ref={parentVideoRef} autoPlay playsInline muted />
              <figcaption>{viewers.find((v) => v.video)?.parentName}</figcaption>
            </figure>
          )}
        </div>

        {shows('chat') && (
        <section className="card agent-card">
          <h2 className="agent-chat-title">Messages</h2>
          <p className="muted small">Message your parent here if you can’t talk or they can’t see your screen.</p>
          <ChatThread messages={messages} onSend={sendChat} meSide="child" placeholder="Message your parent…" />
        </section>
        )}
      </div>


      {whiteboardOpen && (
        <div className="sheet-backdrop whiteboard-backdrop">
          <div className="sheet card whiteboard-sheet" role="dialog" aria-label="Whiteboard">
            <div className="row-between">
              <h2>Whiteboard</h2>
              <button className="btn small" onClick={() => setWhiteboardOpen(false)}>
                Hide
              </button>
            </div>
            <p className="muted small">Your parent opened a shared whiteboard — draw here together.</p>
            <Whiteboard send={wbSend} subscribe={wbSubscribe} />
          </div>
        </div>
      )}
    </main>
  );
}
